/**
 * 비동기 작업을 관리하는 커스텀 훅
 * API 호출 등의 비동기 작업 시 로딩 상태와 에러를 관리합니다.
 */

import { useState } from "react";

/**
 * 비동기 함수를 감싸서 로딩 상태와 에러를 자동으로 관리하는 훅
 * @param {Function} asyncFunction - 비동기 함수 (Promise를 반환하는 함수)
 * @returns {Array} [pending, error, wrappedFunction]
 *   - pending: 비동기 작업이 진행 중인지 여부 (boolean)
 *   - error: 발생한 에러 객체 (에러가 없으면 null)
 *   - wrappedFunction: 원본 비동기 함수를 감싼 래퍼 함수
 *
 * @example
 * const [pending, error, handleFetch] = useAsync(fetchData);
 *
 * // 사용 시
 * const result = await handleFetch();
 * if (pending) return <Loading />;
 * if (error) return <Error message={error.message} />;
 */
function useAsync(asyncFunction) {
  // 로딩 상태를 관리하는 state
  const [pending, setPending] = useState(false);

  // 에러 상태를 관리하는 state
  const [error, setError] = useState(null);

  // 비동기 함수를 감싸는 래퍼 함수
  const wrappedFunction = async (...args) => {
    try {
      // 에러 상태 초기화
      setError(null);

      // 로딩 시작
      setPending(true);

      // 원본 비동기 함수 실행 및 결과 반환
      return await asyncFunction(...args);
    } catch (error) {
      // 에러 발생 시 에러 state에 저장
      setError(error);
      return;
    } finally {
      // 성공/실패 여부와 관계없이 로딩 종료
      setPending(false);
    }
  };

  // [로딩 상태, 에러, 래퍼 함수]를 배열로 반환
  return [pending, error, wrappedFunction];
}

export default useAsync;
